/**
 * Principal distribution debug field definitions
 *
 * ## Data Source Priority
 *
 * Fields prefer `bondAllocations` (calculated at capture time) over `cashFlow.bondPayments`
 * (pre-distribution values). The fallback to cashFlow exists for:
 * - Post-distribution captures (after applyDistributionPlan)
 * - Legacy code paths that don't provide bondAllocations
 *
 * During normal debug capture from the revenue calculator, bondAllocations is always available.
 *
 * ## Decision Context Fields
 *
 * Fields prefixed with `decision.*` capture values AT THE TIME the principal calculation
 * was made (before interest cascading). These show what the algorithm actually saw,
 * which may differ from final cashFlow values due to the backwards processing pattern.
 */

import { roundPrincipalDown } from '~/engine/processing/calculators/core/financial-math/rounding';
import { InterestCalculationEngine } from '~/engine/processing/engines/interest-engine/interest-calculator';
import type { DebugFieldDefinition } from '../types';
import { countBonds, sumBondField } from './extraction-helpers';
import { formatCurrency, formatNumber, formatPercent } from './formatters';

export const principalFields: DebugFieldDefinition[] = [
  {
    id: 'payment.activeBondCount',
    name: 'Active Bonds',
    category: 'payment',
    description: 'Number of active bonds at this payment',
    extractor: (ctx) => countBonds(ctx),
    formatter: (v) => (v !== undefined ? String(v) : 'N/A'),
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'payment.totalPrincipal',
    name: 'Total Principal Amount',
    category: 'payment',
    description: 'Sum of principal distributed across all bonds',
    extractor: (ctx) => sumBondField(ctx, 'principal'),
    formatter: formatCurrency,
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'payment.totalPayment',
    name: 'Total Payment Amount',
    category: 'payment',
    description: 'Sum of all bond payments (principal + interest)',
    extractor: (ctx) => sumBondField(ctx, 'totalPayment'),
    formatter: formatCurrency,
    formula: (ctx) => {
      const principal = sumBondField(ctx, 'principal') ?? 0;
      const interest = sumBondField(ctx, 'totalInterest') ?? 0;
      const total = sumBondField(ctx, 'totalPayment') ?? 0;
      return `Principal ($${principal.toLocaleString()}) + Interest ($${interest.toLocaleString()}) = $${total.toLocaleString()}`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'principal.perBondBreakdown',
    name: 'Per-Bond Principal',
    category: 'principal',
    description: 'Principal distribution per bond series (detailed)',
    extractor: (ctx) => {
      // Prefer bondAllocations if available (during calculation capture)
      if (ctx.bondAllocations) {
        return ctx.bondAllocations.map((alloc) => {
          const bondSeries = ctx.bondSeries.find((bs) => bs.id === alloc.bondSeriesId);
          return {
            bondId: alloc.bondSeriesId,
            name: bondSeries?.name || alloc.bondSeriesId,
            principal: alloc.principal,
          };
        });
      }
      // Fall back to cashFlow (post-distribution captures)
      const cf = ctx.cashFlow;
      if (!cf?.bondPayments) return undefined;
      return cf.bondPayments
        .filter((bp) => bp.isActive)
        .map((bp) => ({
          bondId: bp.bondSeriesId,
          name: bp.bondSeriesName,
          principal: bp.principal,
        }));
    },
    formatter: (v) => {
      if (!Array.isArray(v)) return 'N/A';
      return v.map((b) => `${b.name}: $${b.principal.toLocaleString()}`).join(', ');
    },
    performanceCost: 'medium',
    enabledByDefault: false,
  },
  {
    id: 'principal.distributionHelperSummary',
    name: 'Distribution Helper Summary',
    category: 'principal',
    description: 'Summary of revenue distribution helper map',
    extractor: (ctx) => {
      if (!ctx.helperMap) return undefined;
      return Object.entries(ctx.helperMap).map(([bondId, info]) => ({
        bondId,
        isActive: info.isActive,
        distribution: info.principalDistribution,
        percent: info.revenueDistributionPercent,
        currentPrincipal: info.currentPrincipal,
        remainingCapacity: info.remainingTargetCapacity,
      }));
    },
    formatter: (v) => {
      if (!Array.isArray(v)) return 'N/A';
      return v
        .filter((b) => b.isActive)
        .map((b) => `$${b.distribution.toLocaleString()} (${(b.percent * 100).toFixed(1)}%)`)
        .join(', ');
    },
    performanceCost: 'medium',
    enabledByDefault: false,
  },

  // === Principal Derivation (Recalculated) ===
  // These fields RECALCULATE principal values from the FINAL cashflow state.
  // Formula: Principal = Available / (1 + rate * t)
  // Where t = 0.5 for semi-annual, or 1 if next payment is interest-only.
  //
  // WARNING: These values may DIFFER from actual calculation due to interest cascading!
  // The real calculation uses intermediate values (before cascading).
  // For accurate values, use the decision.* fields which capture values at decision time.
  //
  // NOTE: This simplified formula doesn't account for first-payment scenarios
  // where interest should be calculated from issue date (variable days).

  {
    id: 'principal.maxInterestRate',
    name: 'Max Interest Rate (Active Bonds)',
    category: 'principal',
    description:
      'Maximum interest rate among active bonds - used to calculate principal capacity. ' +
      'The highest rate determines the interest reserve needed.',
    extractor: (ctx) => {
      const cf = ctx.cashFlow;
      if (!cf?.bondPayments) return undefined;

      return cf.bondPayments.reduce((max, bondPayment) => {
        if (!bondPayment.isActive) return max;
        const bondSeries = ctx.bondSeries.find((bs) => bs.id === bondPayment.bondSeriesId);
        if (bondSeries) {
          const actualRate = InterestCalculationEngine.getInterestRateForPayment(
            bondSeries,
            cf.paymentDate
          );
          return Math.max(max, actualRate);
        }
        return max;
      }, 0);
    },
    formatter: formatPercent,
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'principal.interestDivisor',
    name: 'Interest Divisor',
    category: 'principal',
    description:
      'Divisor used in principal formula: (1 + rate) if next is I/O, (1 + rate/2) for semi-annual. ' +
      'This reserves room for interest on assigned principal.',
    extractor: (ctx) => {
      const cf = ctx.cashFlow;
      if (!cf?.bondPayments) return undefined;

      // Get max interest rate
      const maxRate = cf.bondPayments.reduce((max, bondPayment) => {
        if (!bondPayment.isActive) return max;
        const bondSeries = ctx.bondSeries.find((bs) => bs.id === bondPayment.bondSeriesId);
        if (bondSeries) {
          const actualRate = InterestCalculationEngine.getInterestRateForPayment(
            bondSeries,
            cf.paymentDate
          );
          return Math.max(max, actualRate);
        }
        return max;
      }, 0);

      // Check if next payment is I/O
      const nextIndex = ctx.paymentIndex - 1; // Previous in array = next in time
      const nextCashFlow = ctx.candidate.cashFlows[nextIndex];
      const nextIsIO = nextCashFlow?.isInterestOnly ?? false;

      return nextIsIO ? 1 + maxRate : 1 + maxRate / 2;
    },
    formatter: (v) => formatNumber(v, 4),
    formula: (ctx) => {
      const cf = ctx.cashFlow;
      if (!cf?.bondPayments) return undefined;

      const maxRate = cf.bondPayments.reduce((max, bondPayment) => {
        if (!bondPayment.isActive) return max;
        const bondSeries = ctx.bondSeries.find((bs) => bs.id === bondPayment.bondSeriesId);
        if (bondSeries) {
          const actualRate = InterestCalculationEngine.getInterestRateForPayment(
            bondSeries,
            cf.paymentDate
          );
          return Math.max(max, actualRate);
        }
        return max;
      }, 0);

      const nextIndex = ctx.paymentIndex - 1;
      const nextCashFlow = ctx.candidate.cashFlows[nextIndex];
      const nextIsIO = nextCashFlow?.isInterestOnly ?? false;

      if (nextIsIO) {
        return `1 + ${(maxRate * 100).toFixed(2)}% = ${(1 + maxRate).toFixed(4)} (next is I/O, full year)`;
      }
      return `1 + ${(maxRate * 100).toFixed(2)}%/2 = ${(1 + maxRate / 2).toFixed(4)} (semi-annual)`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'principal.rawPrincipalCapacity',
    name: 'Raw Principal Capacity',
    category: 'principal',
    description:
      'Principal before rounding: Combined Revenue / Interest Divisor. ' +
      'This is the maximum principal that leaves room for interest obligations.',
    extractor: (ctx) => {
      // Prefer decisionContext (accurate value at calculation time)
      if (ctx.decisionContext?.rawPrincipalCapacity !== undefined) {
        return ctx.decisionContext.rawPrincipalCapacity;
      }
      // Fallback to recalculation from cashFlow
      const cf = ctx.cashFlow;
      if (!cf?.bondPayments || cf.isInterestOnly) return undefined;

      const currentAvailable = (cf.revenueMinusExistingDebt ?? 0) - (cf.interestObligation ?? 0);
      const nextIndex = ctx.paymentIndex + 1;
      const nextCashFlow = ctx.candidate.cashFlows[nextIndex];
      let combined = currentAvailable;
      if (nextCashFlow?.isInterestOnly) {
        const nextAvailable =
          (nextCashFlow.revenueMinusExistingDebt ?? 0) - (nextCashFlow.interestObligation ?? 0);
        combined += nextAvailable;
      }

      const maxRate = cf.bondPayments.reduce((max, bondPayment) => {
        if (!bondPayment.isActive) return max;
        const bondSeries = ctx.bondSeries.find((bs) => bs.id === bondPayment.bondSeriesId);
        if (bondSeries) {
          const actualRate = InterestCalculationEngine.getInterestRateForPayment(
            bondSeries,
            cf.paymentDate
          );
          return Math.max(max, actualRate);
        }
        return max;
      }, 0);

      const prevIndex = ctx.paymentIndex - 1;
      const prevCashFlow = ctx.candidate.cashFlows[prevIndex];
      const prevIsIO = prevCashFlow?.isInterestOnly ?? false;
      const divisor = prevIsIO ? 1 + maxRate : 1 + maxRate / 2;

      return combined / divisor;
    },
    formatter: formatCurrency,
    formula: (ctx) => {
      const cf = ctx.cashFlow;
      if (!cf?.bondPayments || cf.isInterestOnly) return 'N/A (Interest-only period)';

      // Use decisionContext values for accuracy
      const combined = ctx.decisionContext?.totalRevenueForDistribution;
      const divisor = ctx.decisionContext?.interestDivisor;
      const raw = ctx.decisionContext?.rawPrincipalCapacity;

      // Debug: Check which values are available
      const hasDecisionContext = ctx.decisionContext !== undefined;
      const hasCombined = combined !== undefined;
      const hasDivisor = divisor !== undefined;
      const hasRaw = raw !== undefined;

      if (hasCombined && hasDivisor && hasRaw) {
        return `$${combined.toLocaleString()} ÷ ${divisor.toFixed(4)} = $${raw.toLocaleString()}`;
      }

      // Show diagnostic if falling back
      const missing = [];
      if (!hasDecisionContext) missing.push('decisionContext');
      if (!hasCombined) missing.push('combined');
      if (!hasDivisor) missing.push('divisor');
      if (!hasRaw) missing.push('raw');

      // Fallback to recalculation
      const currentAvailable = (cf.revenueMinusExistingDebt ?? 0) - (cf.interestObligation ?? 0);
      const nextIndex = ctx.paymentIndex + 1;
      const nextCashFlow = ctx.candidate.cashFlows[nextIndex];
      let combinedFallback = currentAvailable;
      if (nextCashFlow?.isInterestOnly) {
        const nextAvailable =
          (nextCashFlow.revenueMinusExistingDebt ?? 0) - (nextCashFlow.interestObligation ?? 0);
        combinedFallback += nextAvailable;
      }

      const maxRate = cf.bondPayments.reduce((max, bondPayment) => {
        if (!bondPayment.isActive) return max;
        const bondSeries = ctx.bondSeries.find((bs) => bs.id === bondPayment.bondSeriesId);
        if (bondSeries) {
          const actualRate = InterestCalculationEngine.getInterestRateForPayment(
            bondSeries,
            cf.paymentDate
          );
          return Math.max(max, actualRate);
        }
        return max;
      }, 0);

      const prevIndex = ctx.paymentIndex - 1;
      const prevCashFlow = ctx.candidate.cashFlows[prevIndex];
      const prevIsIO = prevCashFlow?.isInterestOnly ?? false;
      const divisorFallback = prevIsIO ? 1 + maxRate : 1 + maxRate / 2;
      const rawFallback = combinedFallback / divisorFallback;

      return `[FALLBACK - missing: ${missing.join(', ')}] $${combinedFallback.toLocaleString()} ÷ ${divisorFallback.toFixed(4)} = $${rawFallback.toLocaleString()}`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'principal.roundedPrincipal',
    name: 'Rounded Principal (Final)',
    category: 'principal',
    description:
      'Principal after rounding down to clean amounts. This is what gets distributed to bonds.',
    extractor: (ctx) => {
      // Prefer decisionContext (accurate value at calculation time)
      if (ctx.decisionContext?.roundedPrincipalAvailable !== undefined) {
        return ctx.decisionContext.roundedPrincipalAvailable;
      }
      // Fallback to recalculation from cashFlow
      const cf = ctx.cashFlow;
      if (!cf?.bondPayments || cf.isInterestOnly) return undefined;

      const currentAvailable = (cf.revenueMinusExistingDebt ?? 0) - (cf.interestObligation ?? 0);
      const nextIndex = ctx.paymentIndex + 1;
      const nextCashFlow = ctx.candidate.cashFlows[nextIndex];
      let combined = currentAvailable;
      if (nextCashFlow?.isInterestOnly) {
        const nextAvailable =
          (nextCashFlow.revenueMinusExistingDebt ?? 0) - (nextCashFlow.interestObligation ?? 0);
        combined += nextAvailable;
      }

      const maxRate = cf.bondPayments.reduce((max, bondPayment) => {
        if (!bondPayment.isActive) return max;
        const bondSeries = ctx.bondSeries.find((bs) => bs.id === bondPayment.bondSeriesId);
        if (bondSeries) {
          const actualRate = InterestCalculationEngine.getInterestRateForPayment(
            bondSeries,
            cf.paymentDate
          );
          return Math.max(max, actualRate);
        }
        return max;
      }, 0);

      const prevIndex = ctx.paymentIndex - 1;
      const prevCashFlow = ctx.candidate.cashFlows[prevIndex];
      const prevIsIO = prevCashFlow?.isInterestOnly ?? false;
      const divisor = prevIsIO ? 1 + maxRate : 1 + maxRate / 2;
      const raw = combined / divisor;

      return roundPrincipalDown(raw);
    },
    formatter: formatCurrency,
    formula: (ctx) => {
      const cf = ctx.cashFlow;
      if (!cf?.bondPayments || cf.isInterestOnly) return 'N/A (Interest-only period)';

      // Use decisionContext values for accuracy
      const raw = ctx.decisionContext?.rawPrincipalCapacity;
      const rounded = ctx.decisionContext?.roundedPrincipalAvailable;

      if (raw !== undefined && rounded !== undefined) {
        return `$${raw.toLocaleString()} → $${rounded.toLocaleString()} (rounded to $5,000 increment)`;
      }

      // Fallback to recalculation
      const currentAvailable = (cf.revenueMinusExistingDebt ?? 0) - (cf.interestObligation ?? 0);
      const nextIndex = ctx.paymentIndex + 1;
      const nextCashFlow = ctx.candidate.cashFlows[nextIndex];
      let combined = currentAvailable;
      if (nextCashFlow?.isInterestOnly) {
        const nextAvailable =
          (nextCashFlow.revenueMinusExistingDebt ?? 0) - (nextCashFlow.interestObligation ?? 0);
        combined += nextAvailable;
      }

      const maxRate = cf.bondPayments.reduce((max, bondPayment) => {
        if (!bondPayment.isActive) return max;
        const bondSeries = ctx.bondSeries.find((bs) => bs.id === bondPayment.bondSeriesId);
        if (bondSeries) {
          const actualRate = InterestCalculationEngine.getInterestRateForPayment(
            bondSeries,
            cf.paymentDate
          );
          return Math.max(max, actualRate);
        }
        return max;
      }, 0);

      const prevIndex = ctx.paymentIndex - 1;
      const prevCashFlow = ctx.candidate.cashFlows[prevIndex];
      const prevIsIO = prevCashFlow?.isInterestOnly ?? false;
      const divisor = prevIsIO ? 1 + maxRate : 1 + maxRate / 2;
      const rawFallback = combined / divisor;
      const roundedFallback = roundPrincipalDown(rawFallback);

      return `$${rawFallback.toLocaleString()} → $${roundedFallback.toLocaleString()} (rounded to $5,000 increment)`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },

  // === DECISION CONTEXT FIELDS ===
  // These fields show the ACTUAL values the calculator had at decision time.
  // They use captured decision context (before cascading) rather than recalculating.
  // Compare these to the final cashflow values to understand timing effects.

  {
    id: 'decision.nextInterestObligation',
    name: 'Next Interest Obligation (At Decision)',
    category: 'principal',
    description:
      'Interest obligation of next payment AT THE TIME of principal decision. ' +
      'This may be $0 because cascading has not yet occurred. ' +
      'Compare to final cashflow value to see cascade effect.',
    extractor: (ctx) => ctx.decisionContext?.nextInterestObligation,
    formatter: formatCurrency,
    formula: (ctx) => {
      if (!ctx.decisionContext) return 'Decision context not captured';
      const dc = ctx.decisionContext;
      return `Next (${dc.nextPaymentDate ?? 'none'}): $${(dc.nextInterestObligation ?? 0).toLocaleString()}`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'decision.currentInterestObligation',
    name: 'Current Interest Obligation (At Decision)',
    category: 'principal',
    description:
      'Interest obligation of current payment AT THE TIME of principal decision. ' +
      'This is the value used to calculate available revenue for bonds.',
    extractor: (ctx) => ctx.decisionContext?.currentInterestObligation,
    formatter: formatCurrency,
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'decision.totalRevenueForDistribution',
    name: 'Total Revenue for Distribution (At Decision)',
    category: 'principal',
    description:
      'Combined revenue used for principal calculation at decision time. ' +
      'For I/O periods, includes both current and next period revenue.',
    extractor: (ctx) => ctx.decisionContext?.totalRevenueForDistribution,
    formatter: formatCurrency,
    formula: (ctx) => {
      if (!ctx.decisionContext) return 'Decision context not captured';
      const dc = ctx.decisionContext;
      if (dc.nextIsInterestOnly) {
        return `Current ($${dc.currentAvailableForBonds.toLocaleString()}) + Next I/O ($${(dc.nextAvailableForBonds ?? 0).toLocaleString()}) = $${dc.totalRevenueForDistribution.toLocaleString()}`;
      }
      return `Current only: $${dc.currentAvailableForBonds.toLocaleString()} (next is not I/O)`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'decision.interestDivisor',
    name: 'Interest Divisor (At Decision)',
    category: 'principal',
    description:
      'Divisor used in principal formula at decision time: (1 + rate) if next is I/O, (1 + rate/2) for semi-annual.',
    extractor: (ctx) => ctx.decisionContext?.interestDivisor,
    formatter: (v) => formatNumber(v, 4),
    formula: (ctx) => {
      if (!ctx.decisionContext) return 'Decision context not captured';
      const dc = ctx.decisionContext;
      const rate = dc.maxInterestRate;
      if (dc.nextIsInterestOnly) {
        return `1 + ${(rate * 100).toFixed(2)}% = ${dc.interestDivisor.toFixed(4)} (next is I/O)`;
      }
      return `1 + ${(rate * 100).toFixed(2)}%/2 = ${dc.interestDivisor.toFixed(4)} (semi-annual)`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'decision.maxInterestRate',
    name: 'Max Interest Rate (At Decision)',
    category: 'principal',
    description: 'Maximum interest rate among active bonds at decision time.',
    extractor: (ctx) => ctx.decisionContext?.maxInterestRate,
    formatter: formatPercent,
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'decision.rawPrincipalCapacity',
    name: 'Raw Principal Capacity (At Decision)',
    category: 'principal',
    description:
      'Principal before rounding, calculated at decision time. ' +
      'This is the accurate value used in the calculation.',
    extractor: (ctx) => ctx.decisionContext?.rawPrincipalCapacity,
    formatter: formatCurrency,
    formula: (ctx) => {
      if (!ctx.decisionContext) return 'Decision context not captured';
      const dc = ctx.decisionContext;
      return `$${dc.totalRevenueForDistribution.toLocaleString()} ÷ ${dc.interestDivisor.toFixed(4)} = $${dc.rawPrincipalCapacity.toLocaleString()}`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'decision.roundedPrincipalAvailable',
    name: 'Rounded Principal (At Decision)',
    category: 'principal',
    description:
      'Final rounded principal available for distribution, captured at decision time. ' +
      'This is the actual result returned by determineRevenueAvailableForPrincipal.',
    extractor: (ctx) => ctx.decisionContext?.roundedPrincipalAvailable,
    formatter: formatCurrency,
    formula: (ctx) => {
      if (!ctx.decisionContext) return 'Decision context not captured';
      const dc = ctx.decisionContext;
      return `$${dc.rawPrincipalCapacity.toLocaleString()} → $${dc.roundedPrincipalAvailable.toLocaleString()} (rounded to $5,000)`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'decision.currentAvailableForBonds',
    name: 'Current Available for Bonds (At Decision)',
    category: 'principal',
    description:
      'Revenue available from current record at decision time: revenueMinusDebt - interestObligation.',
    extractor: (ctx) => ctx.decisionContext?.currentAvailableForBonds,
    formatter: formatCurrency,
    formula: (ctx) => {
      if (!ctx.decisionContext) return 'Decision context not captured';
      const dc = ctx.decisionContext;
      return `$${dc.currentRevenueMinusDebt.toLocaleString()} - $${dc.currentInterestObligation.toLocaleString()} = $${dc.currentAvailableForBonds.toLocaleString()}`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'decision.nextAvailableForBonds',
    name: 'Next Available for Bonds (At Decision)',
    category: 'principal',
    description:
      'Revenue available from next record at decision time (if applicable). ' +
      'Only used when next record is interest-only.',
    extractor: (ctx) => ctx.decisionContext?.nextAvailableForBonds,
    formatter: formatCurrency,
    formula: (ctx) => {
      if (!ctx.decisionContext) return 'Decision context not captured';
      const dc = ctx.decisionContext;
      if (dc.nextAvailableForBonds === null) return 'No next record';
      return `$${(dc.nextRevenueMinusDebt ?? 0).toLocaleString()} - $${(dc.nextInterestObligation ?? 0).toLocaleString()} = $${dc.nextAvailableForBonds.toLocaleString()}`;
    },
    performanceCost: 'low',
    enabledByDefault: false, // Lower priority - enable manually if needed
  },
  {
    id: 'decision.isInterestOnly',
    name: 'Is Interest-Only (At Decision)',
    category: 'principal',
    description: 'Whether current payment is an interest-only period (no principal distributed).',
    extractor: (ctx) => ctx.decisionContext?.isInterestOnly,
    formatter: (v) => (v ? 'Yes' : 'No'),
    performanceCost: 'low',
    enabledByDefault: false, // Lower priority
  },

  // === MIN CONSTRAINT DEBUG FIELDS ===
  // These fields show why principal may be constrained by earlier payment capacity.
  // The min constraint prevents allocating principal that would cause negative balances
  // in earlier payments when interest cascades forward (backwards in processing order).

  {
    id: 'constraint.minConstraint',
    name: 'Min Constraint Value',
    category: 'principal',
    description:
      'Maximum principal that can be allocated without causing negative balance in earlier payments. ' +
      'Formula: (minAvailable + drfStartingBalance - buffer) × 2 / maxRate. ' +
      'When this is less than raw capacity, principal is constrained.',
    extractor: (ctx) => ctx.decisionContext?.minConstraint,
    formatter: formatCurrency,
    formula: (ctx) => {
      if (!ctx.decisionContext) return 'Decision context not captured';
      const dc = ctx.decisionContext;
      if (dc.minConstraint === null) return 'Not applicable (no earlier payments)';
      if (dc.minConstraint === Number.POSITIVE_INFINITY) return 'Infinity (no constraint)';
      return `(${formatCurrency(dc.minAvailable ?? 0)} + ${formatCurrency(dc.drfStartingBalanceUsed ?? 0)} - buffer) × 2 / ${formatPercent(dc.maxInterestRate)} = ${formatCurrency(dc.minConstraint)}`;
    },
    performanceCost: 'low',
    enabledByDefault: true, // Critical for debugging
  },
  {
    id: 'constraint.minAvailable',
    name: 'Min Available (Across Earlier Payments)',
    category: 'principal',
    description:
      'The minimum effectiveAvailable found across all earlier payments. ' +
      'This is the bottleneck that limits how much principal can be allocated.',
    extractor: (ctx) => ctx.decisionContext?.minAvailable,
    formatter: formatCurrency,
    performanceCost: 'low',
    enabledByDefault: true, // Critical for debugging
  },
  {
    id: 'constraint.minAvailablePaymentIndex',
    name: 'Min Available Payment Index',
    category: 'principal',
    description:
      'Index of the payment with the minimum effectiveAvailable (the constraining payment).',
    extractor: (ctx) => ctx.decisionContext?.minAvailablePaymentIndex,
    formatter: (v) => (v !== null ? String(v) : 'N/A'),
    performanceCost: 'low',
    enabledByDefault: false, // Secondary detail
  },
  {
    id: 'constraint.minAvailablePaymentDate',
    name: 'Min Available Payment Date',
    category: 'principal',
    description:
      'Date of the payment with the minimum effectiveAvailable (for easier identification).',
    extractor: (ctx) => ctx.decisionContext?.minAvailablePaymentDate,
    formatter: (v) => (typeof v === 'string' ? v : 'N/A'),
    performanceCost: 'low',
    enabledByDefault: true, // Helpful for quick identification
  },
  {
    id: 'constraint.drfStartingBalance',
    name: 'DRF Starting Balance (Static)',
    category: 'principal',
    description:
      'Debt Retirement Fund starting balance from configuration (static value). ' +
      'Compare with Projected DRF to see how much the actual buffer differs.',
    extractor: (ctx) => ctx.decisionContext?.drfStartingBalanceUsed,
    formatter: formatCurrency,
    performanceCost: 'low',
    enabledByDefault: true, // Critical for understanding constraint
  },
  {
    id: 'constraint.projectedDrfAtConstraint',
    name: 'Projected DRF at Constraint',
    category: 'principal',
    description:
      'Projected DRF balance at the constraining payment, accounting for interest obligations ' +
      'from already-allocated (later) payments. This dynamic value replaces the static starting ' +
      'balance in the constraint formula for more accurate principal allocation.',
    extractor: (ctx) => ctx.decisionContext?.projectedDrfAtConstraint,
    formatter: formatCurrency,
    formula: (ctx) => {
      if (!ctx.decisionContext) return 'Decision context not captured';
      const dc = ctx.decisionContext;
      if (dc.projectedDrfAtConstraint === null) return 'Not applicable';
      const startingBal = dc.drfStartingBalanceUsed ?? 0;
      const projectedBal = dc.projectedDrfAtConstraint;
      const diff = projectedBal - startingBal;
      if (Math.abs(diff) < 1) {
        return `${formatCurrency(projectedBal)} (same as starting balance)`;
      }
      return `${formatCurrency(projectedBal)} (${diff >= 0 ? '+' : ''}${formatCurrency(diff)} from starting)`;
    },
    performanceCost: 'low',
    enabledByDefault: true, // Critical for debugging Issue #2 fix
  },
  {
    id: 'constraint.wasConstrainedByMinPayment',
    name: 'Was Constrained by Min Payment',
    category: 'principal',
    description:
      'Whether the min constraint was the limiting factor for principal allocation. ' +
      'True when minConstraint < rawPrincipalCapacity.',
    extractor: (ctx) => ctx.decisionContext?.wasConstrainedByMinPayment,
    formatter: (v) => (v ? 'YES - Principal was limited' : 'No - Raw capacity was lower'),
    performanceCost: 'low',
    enabledByDefault: true, // Critical flag for debugging
  },
];
